import React, { useEffect, useMemo, useState } from "react";
import { useUser } from "../../../../hooks/use-user";
import axios from "../../../../axios";
import ContestHistory from "./ContestHistory";
import ContestCard from "./ContestCard";
import RechargeModal from "../../../../components/Wallet/RechargeModal";
import { AlarmClock, Info, Trophy } from "lucide-react";
import { useLocation, useSearchParams } from "react-router-dom";
import {
  saveContestReturnUrl,
} from "./contestNavigation";

const formatDateInputValue = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
};

const getContestDateValue = (contest) =>
  formatDateInputValue(
    contest?.startTime ||
      contest?.contestDate ||
      contest?.date ||
      contest?.entryTime ||
      contest?.createdOn ||
      contest?.createdAt,
  );

function Maincontest() {
  const { data: user } = useUser();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [data, setData] = useState([]);
  const [activeTab, setActiveTab] = useState(
    searchParams.get("tab") === "history" ? "history" : "live",
  );
  const [selectedDate, setSelectedDate] = useState(
    searchParams.get("date") || "",
  );
  const [, setClickedContests] = useState([]);
  const [showRechargeModal, setShowRechargeModal] = useState(false);

  const fetchContests = async () => {
    try {
      const response = await axios.get("getAllContestForUser");
      if (response?.data) {
        const sortedData = [...response?.data?.result].sort(
          (a, b) =>
            Number(b.isOffer === true) - Number(a.isOffer === true) ||
            new Date(b.createdOn) - new Date(a.createdOn),
        );
        setData(sortedData);
      }
    } catch (error) {
      console.error("Error fetching contests:", error);
    }
  };

  useEffect(() => {
    fetchContests();
  }, []);

  useEffect(() => {
    const nextTab = searchParams.get("tab") === "history" ? "history" : "live";
    const nextDate = searchParams.get("date") || "";

    setActiveTab(nextTab);
    setSelectedDate(nextDate);
  }, [searchParams]);

  useEffect(() => {
    const params = new URLSearchParams();

    if (activeTab !== "live") {
      params.set("tab", activeTab);
    }

    if (selectedDate) {
      params.set("date", selectedDate);
    }

    setSearchParams(params, { replace: true });
  }, [activeTab, selectedDate, setSearchParams]);

  useEffect(() => {
    const returnUrl = `${location.pathname}${location.search}`;
    saveContestReturnUrl(returnUrl);
  }, [location.pathname, location.search]);

  const filteredContests = useMemo(() => {
    if (!selectedDate) return data;
    return data.filter((contest) => getContestDateValue(contest) === selectedDate);
  }, [data, selectedDate]);

  const handleContestPayment = async (
    id,
    entryFee,
    contestName,
    passKey = "",
  ) => {
    if (!user) {
      return {
        success: false,
        message: "Please login to join contest",
      };
    }

    const rechargeBalance = user?.unifiedWallet?.rechargedBalance || 0;
    const contestBalance = user?.unifiedWallet?.contestBalance || 0;
    const referralBalance = user?.unifiedWallet?.referralBalance || 0;
    const lockedBalance = user?.unifiedWallet?.lockedBalance || 0;
    const totalAvailable =
      rechargeBalance + contestBalance + referralBalance + lockedBalance;

    if (totalAvailable < entryFee) {
      setShowRechargeModal(true);
      return {
        success: false,
        insufficient: true,
        message: "Insufficient balance",
      };
    }

    try {
      const response = await axios.post("joinContestWithWallet", {
        contestId: id,
        passKey: passKey.trim(),
      });
      console.log("Join contest response:", response?.data);
      if (response?.data?.success) {
        setClickedContests((prev) => {
          const updated = prev.filter((contestId) => contestId !== id);
          localStorage.setItem("activeContests", JSON.stringify(updated));
          return updated;
        });

        return {
          success: true,
          message: response?.data?.message || "Successfully joined contest",
        };
      }

      return {
        success: false,
        message: response?.data?.message || `Failed to join ${contestName}`,
      };
    } catch (error) {
      console.error("Error joining contest:", error);
      return {
        success: false,
        message:
          error?.response?.data?.message || `Error joining ${contestName}`,
      };
    }
  };

  useEffect(() => {
    const saved = localStorage.getItem("activeContests");
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        if (Array.isArray(parsed)) {
          setClickedContests(parsed);
        }
      } catch {
        if (saved) {
          setClickedContests([saved]);
          localStorage.setItem("activeContests", JSON.stringify([saved]));
          localStorage.removeItem("activeContest");
        }
      }
    }
  }, []);

  return (
    <div className="min-h-screen bg-white pb-20 pt-0 text-white selection:bg-purple-500 selection:text-white">
     
      <div className="mx-auto max-w-6xl px-4 pt-1">
        <div className="mx-auto mb-3 pt-5 lg:pt-14 w-full max-w-[430px]">
         <div className="sticky top-0 z-50 bg-white pb-3">
  <div className="grid grid-cols-2 rounded-full bg-[#EEEEEE] p-1 text-black">
    <button
      type="button"
      onClick={() => setActiveTab("live")}
      className={`rounded-full py-1 text-lg transition-all duration-300 ${
        activeTab === "live"
          ? "bg-[#562566] text-white shadow-md"
          : "text-black"
      }`}
    >
      <span className="text-[#F5A623]">● </span>
      Live
    </button>

    <button
      type="button"
      onClick={() => setActiveTab("history")}
      className={`rounded-full py-1 text-lg transition-all duration-300 ${
        activeTab === "history"
          ? "bg-[#562566] text-white shadow-md"
          : "text-black"
      }`}
    >
      History
    </button>
  </div>
</div>
        </div>

        {activeTab === "live" && (
          <div className="grid grid-cols-1 gap-6 animate-fade-in-up md:grid-cols-2 lg:grid-cols-3">
            {/* <div className="relative">
              <div className="relative overflow-hidden rounded-2xl border border-[#d9cfee] bg-gradient-to-br from-[#f6f4fa] to-[#ece9f3] shadow-[0_10px_30px_rgba(75,45,131,0.15)]">
                <div className="absolute -bottom-10 -left-14 h-40 w-40 rounded-full bg-[#e7dff5] opacity-40"></div>

                <div className="relative p-2">
                  <div className="flex items-start justify-between">
                    <div>
                      <h3 className="text-left text-lg font-bold leading-none text-[#2d2438]">
                        Practice Scorecard Test
                      </h3>

                      <button
                        type="button"
                        className="mt-2 flex items-center gap-2 text-sm text-[#5e556a]"
                      >
                        View Contest Details
                        <Info size={16} className="text-[#34163F]" />
                      </button>
                    </div>

                    <span className="rounded-full bg-green-500 px-3 py-1 text-sm text-white">
                      Live
                    </span>
                  </div>

                  <div className="mb-2 text-right text-sm font-semibold text-[#3b3445]">
                    Duration : 2 min
                  </div>

                  <div className="mb-2 flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="flex h-10 w-10 items-center justify-center rounded-full bg-[#FFF3DD]">
                        <Trophy className="text-[#F2A51A]" size={25} />
                      </div>

                      <div>
                        <p className="text-left text-lg font-bold leading-none text-[#2d2438]">
                          Free Contest
                        </p>
                        <p className="text-sm text-[#756d81]">Total Rewards</p>
                      </div>
                    </div>

                    <button
                      type="button"
                      onClick={() => navigate("/dash/dummy-contest")}
                      className="min-w-[120px] rounded-2xl bg-[#3F234E] px-6 py-3 text-center text-[16px] font-bold leading-tight text-white"
                    >
                      Play
                      <span className="block text-xs font-semibold">
                        Free
                      </span>
                    </button>
                  </div>

                  <div className="mb-2 flex items-center justify-between text-[15px] text-[#756d81]">
                    <span>1/100 slots</span>
                    <span>99 Spots Left</span>
                  </div>

                  <div className="h-1 rounded-full bg-[#d5cfdd]">
                    <div
                      className="h-full rounded-full bg-[#9d96aa]"
                      style={{ width: "1%" }}
                    />
                  </div>
                </div>

                <div
                  className="z-60 bg-[#3F234E] px-2 py-2 text-sm font-bold text-white"
                >
                  <div className="flex items-center justify-between">
                    <span className="flex items-center gap-2">
                      <AlarmClock size={18} />
                      Ends in 01:00
                    </span>
                    <span>Hurry Up!</span>
                  </div>
                </div>
              </div>
            </div> */}

            {filteredContests.length > 0 ? (
              filteredContests.map((e) => (
                <ContestCard
                
                  key={e._id}
                  contest={e}
                  onJoin={handleContestPayment}
                  onWalletUpdated={fetchContests}
                />
              ))
            ) : (
              <div className="rounded-2xl bg-[#F7F4FA] px-4 py-10 text-center text-sm font-semibold text-[#6C6172] md:col-span-2 lg:col-span-3">
                No contests found
              </div>
            )}
          </div>
        )}

        {activeTab === "history" && (
          <div className="animate-fade-in">
            <ContestHistory dateFilter={selectedDate} />
          </div>
        )}
      </div>

      <RechargeModal
        isOpen={showRechargeModal}
        onClose={() => setShowRechargeModal(false)}
        walletBalance={
          (user?.unifiedWallet?.rechargedBalance || 0) +
          (user?.unifiedWallet?.contestBalance || 0) +
          (user?.unifiedWallet?.referralBalance || 0) +
          (user?.unifiedWallet?.lockedBalance || 0)
        }
      />
    </div>
  );
}

export default Maincontest;
